import React, { useState } from 'react';
import { Link } from "gatsby";
import { ReactComponent as ListCategoryIcon } from "../images/icons/list-category.svg";
import { ReactComponent as UserIcon } from "../images/icons/user.svg";
import CategoryMenuList from "./CategoryMenuList";
import useAuth from "../hooks/useAuth";
import { handleLogout } from "../utils/auth";

const PRODUCT_CATALOGUE_URL = "https://multiquip.seeksocial.site/wp-content/uploads/2026/05/MQ-PRODUCT-CATALOGUE-202627-V7-PRINT-READY.pdf";

const SideMenu = ({ isOpen, onClose }) => {
  const { loggedIn } = useAuth();
  const [activeMenuTab, setActiveMenuTab] = useState('category');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const accountAccessPath = loggedIn ? '/account' : '/sign-in';
  const accountAccessLabel = loggedIn ? 'View Account' : 'Sign In/Register';

  const brands = [
    { label: 'MQ Whiteman', slug: 'mq-whiteman' },
    { label: 'MQ Mikasa', slug: 'mikasa' },
    { label: 'MQ', slug: 'mq' },
    { label: 'Somero', slug: 'somero' },
    { label: 'Relly', slug: 'relly' },
    { label: 'Magic Screed', slug: 'magic-screed' },
    { label: 'Kraft', slug: 'kraft' },
    { label: 'Makinex', slug: 'makinex' },
    { label: 'HAMEVAC', slug: 'hamevac' },
    { label: 'ALMI', slug: 'almi' },
    { label: 'IQ Power Tools', slug: 'iq-power-tools' },
  ];

  const pages = [
    { label: 'About Multiquip UK', to: '/about' },
    { label: 'MQ Hire', to: '/hire' },
    { label: 'FAQs', to: '/faqs' },
    { label: 'Knowledge Hub', to: '/knowledge-base' },
    { label: 'Contact', to: '/contact' },
    { label: 'Blogs', to: '/blog-listing' },
    { label: 'Download Multiquip Product Catalogue', href: PRODUCT_CATALOGUE_URL, isDownload: true },
  ];

  const accountItems = [
    { label: 'My Account', to: '/account' },
    { label: 'Track Your Order', to: '/account/orders' },
    { label: accountAccessLabel, to: accountAccessPath },
  ];

  const onLogoutClick = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    try {
      onClose?.();
      await handleLogout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      {/* Sidebar Menu */}
      <div
        className={`fixed desktop-lg:hidden left-0 z-40 w-[318px] bg-white shadow-shadow-1 overflow-hidden transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          top: 'var(--header-height, 0px)',
          height: 'calc(100vh - var(--header-height, 0px))',
        }}
      >
        <nav className="flex flex-col h-full">
          {/* Tab Navigation */}
          <div className="flex items-center border-b border-mqgrey2 bg-white z-10">
            <button
              onClick={() => setActiveMenuTab('category')}
              className={`flex items-center justify-center gap-[8px] py-[12px] border-b-[3px] transition-colors ${
                activeMenuTab === 'category'
                  ? 'border-mqred7 text-mqred7 basis-[52%] flex-grow px-[8px]'
                  : 'border-transparent text-mqgrey4 hover:text-mqgrey5 basis-[24%] px-[8px]'
              }`}
            >
              <ListCategoryIcon className="w-[20px] h-[20px] fill-none stroke-current" />
              {activeMenuTab === 'category' && (
                <span className="text-_1424 font-work-sans font-medium">Products</span>
              )}
            </button>
            <button
              onClick={() => setActiveMenuTab('page')}
              className={`flex items-center justify-center gap-[8px] py-[12px] border-b-[3px] transition-colors ${
                activeMenuTab === 'page'
                  ? 'border-mqred7 text-mqred7 basis-[52%] flex-grow px-[16px]'
                  : 'border-transparent text-mqgrey4 hover:text-mqgrey5 basis-[24%] px-[8px]'
              }`}
            >
              <svg className="w-[20px] h-[20px] fill-none stroke-current" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {activeMenuTab === 'page' && (
                <span className="text-_1424 font-work-sans font-medium">Page</span>
              )}
            </button>
            <button
              onClick={() => setActiveMenuTab('account')}
              className={`flex items-center justify-center gap-[8px] py-[12px] border-b-[3px] transition-colors ${
                activeMenuTab === 'account'
                  ? 'border-mqred7 text-mqred7 basis-[52%] flex-grow px-[16px]'
                  : 'border-transparent text-mqgrey4 hover:text-mqgrey5 basis-[24%] px-[8px]'
              }`}
            >
              <UserIcon className="w-[20px] h-[20px] fill-none stroke-current" />
              {activeMenuTab === 'account' && (
                <span className="text-_1424 font-work-sans font-medium">Account</span>
              )}
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            {/* Category Tab Content */}
            {activeMenuTab === 'category' && (
              <div className="py-[16px] px-[16px]">
                <div className="mb-[24px]">
                  <h3 className="text-_1828 font-work-sans font-semibold text-mqgrey5 mb-[16px]">Category</h3>
                  <CategoryMenuList variant="accordion" />
                </div>

                {/* Brands Section */}
                <div>
                  <h3 className="text-_1828 font-work-sans font-semibold text-mqgrey5 mb-[12px]">Brand</h3>
                  <div className="flex flex-wrap gap-[8px]">
                    {brands.map((brand) => (
                      <Link
                        key={brand.slug}
                        to={`/shop-brand/${encodeURIComponent(brand.slug)}`}
                        className="px-[12px] py-[6px] bg-mqgrey1 text-_1218 font-work-sans font-medium text-mqgrey5 rounded-[4px] hover:bg-mqred1 hover:text-mqred7 transition-colors"
                        onClick={onClose}
                      >
                        {brand.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Page Tab Content */}
            {activeMenuTab === 'page' && (
              <div className="py-[16px] px-[16px]">
                <div className="space-y-[12px]">
                  {pages.map((page, index) => (
                    page.isDownload ? (
                      <a
                        key={index}
                        href={page.href}
                        download
                        className="block px-[12px] py-[10px] text-_1624 font-work-sans font-normal text-mqgrey5 hover:text-mqred7 transition-colors"
                        onClick={onClose}
                      >
                        {page.label}
                      </a>
                    ) : (
                      <Link
                        key={index}
                        to={page.to}
                        className="block px-[12px] py-[10px] text-_1624 font-work-sans font-normal text-mqgrey5 hover:text-mqred7 transition-colors"
                        onClick={onClose}
                      >
                        {page.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* Account Tab Content */}
            {activeMenuTab === 'account' && (
              <div className="py-[16px] px-[16px]">
                <div className="space-y-[12px]">
                  {accountItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.to}
                      className="block px-[12px] py-[10px] text-_1624 font-work-sans font-normal text-mqgrey5 hover:text-mqred7 transition-colors"
                      onClick={onClose}
                    >
                      {item.label}
                    </Link>
                  ))}
                  {loggedIn ? (
                    <button
                      type="button"
                      onClick={onLogoutClick}
                      disabled={isLoggingOut}
                      className="block w-full px-[12px] py-[10px] text-left text-_1624 font-work-sans font-normal text-mqgrey5 hover:text-mqred7 transition-colors"
                    >
                      {isLoggingOut ? "Logging out..." : "Logout"}
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </nav>
      </div>

      {/* Overlay - click to close */}
      {isOpen && (
        <div
          className="fixed desktop-lg:hidden left-0 right-0 z-30 bg-black bg-opacity-50 transition-opacity duration-300"
          style={{
            top: 'var(--header-height, 0px)',
            height: 'calc(100vh - var(--header-height, 0px))',
          }}
          onClick={onClose}
        />
      )}
    </>
  );
};

export default SideMenu;
